import { useState, useEffect, useRef, useCallback } from "react";
import { useBluelagoon } from "@bluelagoon";

/**
 * Issues and refreshes a short-lived, workflow-scoped JWT for previewing the
 * authoring user's own workflow. Replaces putting the platform Bearer token
 * in the iframe URL as `x_token_auth`. The token is delivered to the app via
 * `?x_deep_link_token=` (the same channel used by the existing impersonation
 * / cross-app deep-link flow).
 *
 * Tokens live ~1h server-side; we proactively re-fetch ~5min before expiry.
 */
export default function useWorkflowPreviewToken(workflowId) {
    const [token, setToken] = useState(null);
    const expiresAtRef = useRef(null);
    const inflightRef = useRef(null);
    const { call: issueToken } = useBluelagoon({
        method: "issueWorkflowPreviewToken",
    });

    const fetchToken = useCallback(async () => {
        if (!workflowId) return null;
        if (inflightRef.current) return inflightRef.current;
        const promise = issueToken({ workflowId })
            .then((res) => {
                const payload = res?.data?.data || res?.data || {};
                if (payload?.token) {
                    setToken(payload.token);
                    expiresAtRef.current = payload.expiresAt
                        ? new Date(payload.expiresAt).getTime()
                        : Date.now() + 60 * 60 * 1000;
                    return payload.token;
                }
                return null;
            })
            .catch((err) => {
                console.error("Failed to issue workflow preview token:", err);
                return null;
            })
            .finally(() => {
                inflightRef.current = null;
            });
        inflightRef.current = promise;
        return promise;
    }, [workflowId, issueToken]);

    useEffect(() => {
        setToken(null);
        expiresAtRef.current = null;
        if (workflowId) fetchToken();
    }, [workflowId, fetchToken]);

    useEffect(() => {
        if (!token) return undefined;
        const refreshAt = (expiresAtRef.current || 0) - 5 * 60 * 1000;
        const delay = Math.max(refreshAt - Date.now(), 30 * 1000);
        const id = setTimeout(() => fetchToken(), delay);
        return () => clearTimeout(id);
    }, [token, fetchToken]);

    // Detached "open in new tab" can't be refreshed by the parent, so it
    // gets a long-lived (7-day) token instead of the 1-hour iframe token.
    // Always fetched fresh per click — never cached.
    const issueLongLivedToken = useCallback(async () => {
        if (!workflowId) return null;
        try {
            const res = await issueToken({ workflowId, scope: "newTab" });
            const payload = res?.data?.data || res?.data || {};
            return payload?.token || null;
        } catch (err) {
            console.error("Failed to issue long-lived workflow token:", err);
            return null;
        }
    }, [workflowId, issueToken]);

    return { token, refresh: fetchToken, issueLongLivedToken };
}
